import { useCallback, useMemo } from "react";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { useNotifyStatus } from "contexts/status";
import { useTenantTranslation } from "hooks/formatters";

interface UseProductionBasketRowUpdaterParams<TRow extends { target: number }> {
  maxTarget?: number;
  onValidValue: (row: TRow, value: number) => void;
  getDefaultValue?: (row: TRow) => number;
}

export const useProductionBasketRowUpdater = <TRow extends { target: number }>({
  maxTarget,
  onValidValue,
  getDefaultValue,
}: UseProductionBasketRowUpdaterParams<TRow>) => {
  const notifyStatus = useNotifyStatus();
  const { t } = useTenantTranslation();
  const serialiserOptions = useMemo(
    () => ({
      min: 0,
      max: maxTarget,
      default: { value: null, text: "" },
      decimalPlaces: 0,
    }),
    [maxTarget]
  );
  const { parse } = useNumberSerialiser(serialiserOptions);

  return useCallback(
    (newRow: TRow, oldRow: TRow): TRow => {
      const parsed = parse(String(newRow.target));
      if (!parsed.valid) {
        notifyStatus({ text: t("invalidNumber"), type: "error" });
        return oldRow;
      }

      const value =
        parsed.value ??
        (getDefaultValue ? getDefaultValue(oldRow) : oldRow.target);
      onValidValue(oldRow, value);
      return { ...newRow, target: value };
    },
    [getDefaultValue, notifyStatus, onValidValue, parse, t]
  );
};
